import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import Card from "@mui/material/Card";
import DataTable from "e4acomponents/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import OrganizationService from "services/organizations.service";
import ConfirmDialog from "./confirmDialog";

import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import { Can } from "Can";
import { subject } from "@casl/ability";

import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import Footer from "e4acomponents/Footer";

const OrganizationConfig = () => {
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
    const [selectedOrganizationName, setSelectedOrganizationName] = useState("");
    const [actionType, setActionType] = useState("");
    const [hasError, setHasError] = useState(false); // Track if error should be displayed

    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    useEffect(() => {
        //fetch organization from backend
        const fetchOrganizations = async () => {
            try {
                setIsLoading(true);
                const response = await OrganizationService.getOrganizations();
                setTableData(getRows(response));
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching organizations");
            }
        };
        fetchOrganizations();
    }, []);

    const clickEditHandler = (id, name) => {
        setSelectedOrganizationId(id);
        setSelectedOrganizationName(name);
        setActionType("edit");
        setOpenDialog(true);
    };

    const clickAddHandler = () => {
        setActionType("create");
        setOpenDialog(true);
    };

    const handleDeleteClick = (id) => {
        setSelectedOrganizationId(id);
        setActionType("delete");
        setOpenDialog(true);
    };

    const handleDialogAction = async () => {
        if (actionType === "delete") {
            if (!selectedOrganizationId) return;
            try {
                await OrganizationService.deleteOrganization(selectedOrganizationId);
                const response = await OrganizationService.getOrganizations();
                setTableData(getRows(response));
                setOpenDialog(false);
            } catch (err) {
                console.error(err);
            }
            setOpenDialog(false);
        } else if (actionType === "edit") {
            if (!selectedOrganizationId) return;
            if (!selectedOrganizationName) {
                setHasError(true);
                return;
            }
            try {
                await OrganizationService.editOrganization(selectedOrganizationId, {
                    name: selectedOrganizationName,
                });
                const response = await OrganizationService.getOrganizations();
                setTableData(getRows(response));
                setHasError(false);
                setOpenDialog(false);
            } catch (error) {
                console.error(error);
            }
        } else if (actionType === "create") {
            if (!selectedOrganizationName) {
                setHasError(true);
                return;
            }
            try {
                await OrganizationService.createOrganization({
                    name: selectedOrganizationName,
                });
                const response = await OrganizationService.getOrganizations();
                setTableData(getRows(response));
                setHasError(false);
                setOpenDialog(false);
            } catch (error) {
                console.error(err);
            }
        } else {
            return;
        }
    };

    const getRows = (info) => {
        return info.map((row) => {
            return {
                type: "users",
                id: row._id,
                org_id: row.org_id,
                name: row.name,
            };
        });
    };

    const dataTableData = {
        columns: [
            { Header: "ID", accessor: "org_id", width: "30%" },
            { Header: "Name", accessor: "name", width: "50%" },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                Cell: ({ cell }) => {
                    const organizationId = cell.row.original.id;
                    const organizationName = cell.row.original.name;
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can("delete", "organizations") && (
                                <Tooltip title="Delete Organization">
                                    <IconButton onClick={() => handleDeleteClick(organizationId)}>
                                        <DeleteTwoToneIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {ability.can("edit", "organizations") && (
                                <Tooltip title="Edit Organization">
                                    <IconButton
                                        onClick={() =>
                                            clickEditHandler(organizationId, organizationName)
                                        }
                                    >
                                        <EditTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    );
                },
            },
        ],
        rows: tableData,
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <MDBox mb={3}>
                    <Card id="organization-management">
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Organization Management
                            </MDTypography>
                            {ability.can("create", "organizations") && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    onClick={clickAddHandler}
                                >
                                    + Add Organization
                                </MDButton>
                            )}
                        </MDBox>
                        <DataTable table={dataTableData} isLoading={isLoading} tableId="organization-management-table" />
                    </Card>
                </MDBox>
                <ConfirmDialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    onConfirm={handleDialogAction}
                    actionType={actionType}
                    selectedOrganizationName={selectedOrganizationName}
                    setSelectedOrganizationName={setSelectedOrganizationName}
                    hasError={hasError} // Pass the error state to the dialog
                    setHasError={setHasError} // Pass the function to reset the error state
                />
            </MDBox>
            <Footer />
        </DashboardLayout>

    );
};

export default OrganizationConfig;